import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { getRecentRequestTrace } from "@/lib/requestTrace";

function newIncidentId(): string {
  // Short, human-quotable id for support tickets — 8 chars of crypto
  // randomness rendered as base32-ish. We don't need uniqueness across
  // the universe, just enough that a user can read it back over the
  // phone without ambiguity.
  const bytes = new Uint8Array(5);
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 8).toUpperCase();
}

// Route-scoped error boundary. The global AppErrorBoundary at main.tsx
// catches catastrophic errors and replaces the whole app with a refresh
// fallback (loses session, layout, everything). This boundary sits
// inside the Routes tree so a render-time exception or chunk-load
// failure on a single page renders a small inline fallback while
// keeping the topbar / nav / auth state intact — the user can navigate
// to a different page without a full reload.
//
// Resets automatically when the route changes (the parent passes the
// current pathname as a prop; getDerivedStateFromProps clears the error
// state on pathname change).

interface Props {
  pathname: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
  pathname: string;
  incidentId: string | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = {
    hasError: false,
    message: null,
    pathname: this.props.pathname,
    incidentId: null,
  };

  static getDerivedStateFromError(err: unknown): Partial<State> {
    return {
      hasError: true,
      message: err instanceof Error ? err.message : String(err),
      incidentId: newIncidentId(),
    };
  }

  static getDerivedStateFromProps(
    nextProps: Props,
    prevState: State,
  ): Partial<State> | null {
    if (nextProps.pathname !== prevState.pathname) {
      return {
        hasError: false,
        message: null,
        pathname: nextProps.pathname,
        incidentId: null,
      };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    const payload = {
      incident_id: this.state.incidentId,
      message: error.message,
      stack: error.stack ?? null,
      component_stack: info.componentStack ?? null,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      scope: "route",
      recent_requests: getRecentRequestTrace(),
    };
    try {
      void fetch("/v1/client-errors", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // fetch not available — ignore.
    }
  }

  private retry = () => {
    this.setState({ hasError: false, message: null, incidentId: null });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4 py-10 text-center"
      >
        <h2 className="text-lg font-semibold">This page couldn't load</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Something went wrong rendering this page. The incident has been
          reported. You can retry, or use the navigation to go elsewhere.
        </p>
        {this.state.incidentId && (
          <p className="text-xs text-muted-foreground">
            Reference: <code className="font-mono">{this.state.incidentId}</code>
          </p>
        )}
        {this.state.message && (
          <pre className="max-w-xl overflow-auto rounded-md border bg-muted p-2 text-left text-xs">
            {this.state.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={this.retry}>
            Try again
          </Button>
          <Button
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}
